import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SearchResult {
  title: string;
  link: string;
  snippet: string;
  source: string;
  publishedDate?: string;
  images?: Array<{ url: string; caption?: string }>;
}

export interface AnalyzedResult extends SearchResult {
  sentiment: 'positive' | 'neutral' | 'negative';
  sentimentScore: number;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

export interface SentimentBreakdown {
  positive: number;
  neutral: number;
  negative: number;
  overallScore: number;
  overallSentiment: 'positive' | 'neutral' | 'negative';
}

export interface SignalAlert {
  type: 'injury' | 'controversy' | 'contract' | 'achievement';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
}

export interface AthleteSignalIntel {
  athleteId: string;
  athleteName: string;
  analyzedResults: AnalyzedResult[];
  sentimentBreakdown: SentimentBreakdown;
  alerts: SignalAlert[];
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
}

// Simple in-memory cache for signal data
const signalCache = new Map<string, { data: AthleteSignalIntel; timestamp: number }>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export function useAthleteSignalIntel() {
  const [signalData, setSignalData] = useState<AthleteSignalIntel | null>(() => {
    // Hydrate from cache on mount
    let newest: { data: AthleteSignalIntel; timestamp: number } | null = null;
    signalCache.forEach((entry) => {
      if (Date.now() - entry.timestamp < CACHE_DURATION && (!newest || entry.timestamp > newest.timestamp)) {
        newest = entry;
      }
    });
    return newest ? (newest as { data: AthleteSignalIntel; timestamp: number }).data : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchSignalIntel = useCallback(async (
    athleteId: string,
    athleteName: string,
    teamName?: string,
    sport?: string,
    forceRefresh = false,
    espnId?: number
  ): Promise<AthleteSignalIntel> => {
    // Check cache first
    const cacheKey = athleteId;
    const cached = signalCache.get(cacheKey);
    
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setSignalData(cached.data);
      return cached.data;
    }

    setIsLoading(true);
    
    const emptyResult: AthleteSignalIntel = {
      athleteId,
      athleteName,
      analyzedResults: [],
      sentimentBreakdown: { positive: 0, neutral: 0, negative: 0, overallScore: 0, overallSentiment: 'neutral' },
      alerts: [],
      lastUpdated: null,
      isLoading: false,
      error: null
    };

    try {
      // Step 1: Fetch ESPN news (free, no API key needed)
      const { data: espnData, error: espnError } = await supabase.functions.invoke('fetch-espn-athlete-news', {
        body: { espnId, athleteName, teamName, sport }
      });

      if (espnError) {
        console.error('ESPN news fetch error:', espnError);
        throw new Error(espnError.message);
      }

      // Convert ESPN articles to SearchResult format
      const searchResults: SearchResult[] = (espnData?.articles || []).map((article: any) => ({
        title: article.headline,
        link: article.link,
        snippet: article.description || '',
        source: 'ESPN',
        publishedDate: article.published,
        images: article.images
      }));

      if (searchResults.length === 0) {
        const noResultsData = {
          ...emptyResult,
          lastUpdated: new Date(),
        };
        setSignalData(noResultsData);
        signalCache.set(cacheKey, { data: noResultsData, timestamp: Date.now() });
        return noResultsData;
      }

      // Step 2: Analyze sentiment using existing AI endpoint
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke('analyze-athlete-signal', {
        body: { searchResults, athleteName }
      });

      if (analysisError) {
        console.error('Sentiment analysis error:', analysisError);
        // If AI analysis fails, return articles without sentiment
        const basicResults: AnalyzedResult[] = searchResults.map(r => ({
          ...r,
          sentiment: 'neutral' as const,
          sentimentScore: 0,
          category: 'news',
          priority: 'low' as const
        }));
        
        const fallbackResult: AthleteSignalIntel = {
          athleteId,
          athleteName,
          analyzedResults: basicResults,
          sentimentBreakdown: { positive: 0, neutral: searchResults.length, negative: 0, overallScore: 0, overallSentiment: 'neutral' },
          alerts: [],
          lastUpdated: new Date(),
          isLoading: false,
          error: null
        };
        
        setSignalData(fallbackResult);
        signalCache.set(cacheKey, { data: fallbackResult, timestamp: Date.now() });
        return fallbackResult;
      }

      const result: AthleteSignalIntel = {
        athleteId,
        athleteName,
        analyzedResults: analysisData?.analyzedResults || [],
        sentimentBreakdown: analysisData?.sentimentBreakdown || emptyResult.sentimentBreakdown,
        alerts: analysisData?.alerts || [],
        lastUpdated: new Date(),
        isLoading: false,
        error: null
      };

      setSignalData(result);
      signalCache.set(cacheKey, { data: result, timestamp: Date.now() });
      
      return result;

    } catch (error) {
      console.error('Error fetching signal intel:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch signal intelligence';
      
      toast({
        title: 'Signal Intelligence Error',
        description: errorMessage,
        variant: 'destructive'
      });

      const errorResult = {
        ...emptyResult,
        error: errorMessage,
        lastUpdated: new Date()
      };
      
      setSignalData(errorResult);
      return errorResult;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const getQuickSignal = useCallback((athleteId: string): 'positive' | 'neutral' | 'negative' | null => {
    const cached = signalCache.get(athleteId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data.sentimentBreakdown.overallSentiment;
    }
    return null;
  }, []);

  const clearCache = useCallback((athleteId?: string) => {
    if (athleteId) {
      signalCache.delete(athleteId);
    } else {
      signalCache.clear();
    }
  }, []);

  return {
    signalData,
    isLoading,
    fetchSignalIntel,
    getQuickSignal,
    clearCache
  };
}

export default useAthleteSignalIntel;
